import classNames from "classnames";
import React from "react";
import { Svg, SVG_ICONS } from "../Svg/Svg";
import { Typography } from "../Typography/Typography";
import { TTSButton } from "../_controls/TTSButton/TTSButton";

import "./StaticModal.scss";
import { useTranslation } from "react-i18next";

/**
 * Interface for StaticModal sub-components.
 *
 * @description Defines the sub-components available on the StaticModal component.
 */
interface ModalSubComponents {
  Header: React.FC<HeaderProps>;
  Title: React.FC<TitleProps>;
  Body: React.FC<BodyProps>;
  Footer: React.FC<FooterProps>;
}

/**
 * Props for the StaticModal Header component.
 *
 * @description The Header component is used as the top section of a StaticModal,
 * typically containing the title and close button.
 *
 * @property {React.ReactNode} [children] - Content to render in the header (usually StaticModal.Title).
 * @property {() => void} [onHide] - Function called when the close button is clicked.
 *
 * @example
 * <StaticModal.Header onHide={handleClose}>
 *   <StaticModal.Title>Modal Title</StaticModal.Title>
 * </StaticModal.Header>
 */
interface HeaderProps {
  children?: React.ReactNode;
  onHide?: () => void;
}

/**
 * Props for the StaticModal Title component.
 *
 * @description The Title component displays the modal title with an optional TTS (Text-to-Speech) button.
 *
 * @property {string} [children] - The title text to display.
 * @property {string} [ttsButtonId] - HTML id attribute for the TTS button.
 *
 * @example
 * <StaticModal.Title ttsButtonId="static-modal-tts">Settings</StaticModal.Title>
 */
interface TitleProps {
  children?: string;
  ttsButtonId?: string;
}

/**
 * Props for the StaticModal Body component.
 *
 * @description The Body component contains the main content of the modal.
 *
 * @property {string} [id] - HTML id attribute for the body container.
 * @property {React.ReactNode} [children] - Content to render in the modal body.
 * @property {boolean} [isNoFooter=false] - Whether the modal has no footer.
 *   When true, applies additional bottom padding.
 *
 * @example
 * <StaticModal.Body>
 *   <p>Modal content goes here</p>
 * </StaticModal.Body>
 */
interface BodyProps {
  id?: string;
  children?: React.ReactNode;
  isNoFooter?: boolean;
}

/**
 * Props for the StaticModal Footer component.
 *
 * @description The Footer component is used for the bottom section of a StaticModal,
 * typically containing action buttons.
 *
 * @property {React.ReactNode} [children] - Content to render in the footer (usually buttons).
 *
 * @example
 * <StaticModal.Footer>
 *   <Button title="Cancel" onClick={handleClose} />
 *   <Button title="Save" type="primary" onClick={handleSave} />
 * </StaticModal.Footer>
 */
interface FooterProps {
  children?: React.ReactNode;
}

/**
 * Props for the StaticModal component.
 *
 * @description The StaticModal component is a simplified modal dialog without overlay
 * or body scroll locking. Useful for embedded modals or nested modal scenarios.
 *
 * @property {boolean} show - Whether the modal is visible. Required.
 * @property {React.ReactNode} [children] - Modal content (Header, Body, Footer components).
 * @property {string} [className] - Additional CSS class names for the modal body.
 * @property {string} [wrapperClassName] - Additional CSS class names for the modal wrapper.
 * @property {boolean} [autoHeight=false] - Whether the modal should auto-size its height.
 * @property {string} [id] - HTML id attribute for the modal wrapper.
 * @property {string} [modalBodyId] - HTML id attribute for the modal body container.
 * @property {number} [tabIndex] - Tab index for keyboard navigation.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic static modal
 * <StaticModal show={isOpen}>
 *   <StaticModal.Header onHide={() => setIsOpen(false)}>
 *     <StaticModal.Title>Confirm Action</StaticModal.Title>
 *   </StaticModal.Header>
 *   <StaticModal.Body>
 *     <p>Are you sure you want to proceed?</p>
 *   </StaticModal.Body>
 *   <StaticModal.Footer>
 *     <Button title="Cancel" onClick={() => setIsOpen(false)} />
 *     <Button title="Confirm" type="primary" onClick={handleConfirm} />
 *   </StaticModal.Footer>
 * </StaticModal>
 */
interface Props {
  show: boolean;
  children?: React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  autoHeight?: boolean;
  id?: string;
  modalBodyId?: string;
  tabIndex?: number;
  testId?: string;
}

/**
 * A static modal dialog component without overlay.
 *
 * @description StaticModal renders a modal dialog without the overlay background and
 * body scroll locking. This is useful for embedded modals, nested modal scenarios,
 * or when you need more control over the modal behavior.
 *
 * Features:
 * - Compound component pattern for flexible composition
 * - No overlay background (unlike Modal component)
 * - No body scroll lock management
 * - Auto-height mode for dynamic content
 * - Text-to-speech support via TTSButton
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A modal dialog without overlay
 *
 * @see Props for all available properties
 * @see Header for modal header component
 * @see Title for modal title component
 * @see Body for modal body component
 * @see Footer for modal footer component
 */
const StaticModal: React.FC<Props> & ModalSubComponents = (props) => {
  const {
    show,
    children,
    className = "",
    wrapperClassName = "",
    autoHeight = false,
    id,
    modalBodyId,
    tabIndex,
    testId,
  } = props;
  const wrapClass = classNames(
    "static-modal-wrapper",
    { open: !!show },
    wrapperClassName
  );
  const bodyClass = classNames("modal", {
    [className]: !!className,
    "auto-height": autoHeight,
  });

  return (
    <div className={wrapClass} id={id} tabIndex={tabIndex} data-testid={testId}>
      {/* <Overlay open={show} /> */}
      <div className="modal-content">
        <div className={bodyClass} id={modalBodyId}>
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * The header section of a StaticModal component.
 *
 * @description Header renders the top section of a modal with a close button.
 * It typically contains the StaticModal.Title component and provides the close functionality.
 *
 * @param {HeaderProps} props - The component props
 * @returns {JSX.Element} A header div with close button
 *
 * @see HeaderProps for all available properties
 */
const Header: React.FC<HeaderProps> = (props) => {
  const { t } = useTranslation();
  return (
    <div className="modal-header">
      {props.children}
      <button className="modal-header__close__btn" onClick={props.onHide}>
        <Svg
          icon={SVG_ICONS.DRAWER_CLOSE}
          width={24}
          height={24}
          title={t("PRIVACY_COMMON_CLOSE_ALT_TEXT")}
        />
      </button>
    </div>
  );
};

/**
 * The title component for a StaticModal header.
 *
 * @description Title renders the modal title text with an optional TTS (Text-to-Speech) button
 * for accessibility. Uses Typography component for consistent text styling.
 *
 * @param {TitleProps} props - The component props
 * @returns {JSX.Element} A title div with optional TTS button
 *
 * @see TitleProps for all available properties
 */
const Title: React.FC<TitleProps> = (props) => (
  <div className="modal-title">
    <Typography variant="title2">
      {props.children}
      {props.children && (
        <TTSButton
          value={props.children}
          className="tts-button-modal"
          id={props.ttsButtonId}
        />
      )}
    </Typography>
  </div>
);

/**
 * The body section of a StaticModal component.
 *
 * @description Body renders the main content area of the modal. It supports
 * a no-footer mode that adds additional bottom padding when no footer is present.
 *
 * @param {BodyProps} props - The component props
 * @returns {JSX.Element} A body div for modal content
 *
 * @see BodyProps for all available properties
 */
const Body: React.FC<BodyProps> = (props) => {
  const { isNoFooter = false, id } = props;
  return (
    <div
      className={classNames("modal-body", { "no-footer": isNoFooter })}
      id={id}
    >
      {props.children}
    </div>
  );
};

/**
 * The footer section of a StaticModal component.
 *
 * @description Footer renders the bottom section of the modal, typically used
 * for action buttons like Save, Cancel, etc.
 *
 * @param {FooterProps} props - The component props
 * @returns {JSX.Element} A footer div for modal actions
 *
 * @see FooterProps for all available properties
 */
const Footer: React.FC<FooterProps> = (props) => (
  <div className="modal-footer">{props.children}</div>
);

StaticModal.Header = Header;
StaticModal.Title = Title;
StaticModal.Body = Body;
StaticModal.Footer = Footer;

export default StaticModal;
